import React from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { Drill } from '../../types/drill';
import DrillSheet from './DrillSheet';
import DrillThumbnail from './DrillThumbnail';

interface DrillDetailModalProps {
  drill: Drill;
  onClose: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

const Meta: React.FC<{ label: string; value?: string | null }> = ({ label, value }) =>
  value ? (
    <div>
      <dt className="text-xs text-gray-400">{label}</dt>
      <dd className="text-sm text-gray-900">{value}</dd>
    </div>
  ) : null;

const DrillDetailModal: React.FC<DrillDetailModalProps> = ({ drill, onClose, onEdit, onDelete }) => (
  <DrillSheet
    title={drill.title}
    onClose={onClose}
    maxWidthClass="md:max-w-3xl"
    footer={
      drill.canModify && (onEdit || onDelete) ? (
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-red-200 px-4 py-2 text-sm text-red-700 hover:bg-red-50"
            >
              <TrashIcon className="h-4 w-4" />
              Törlés
            </button>
          )}
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex items-center justify-center gap-1.5 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
            >
              <PencilIcon className="h-4 w-4" />
              Szerkesztés
            </button>
          )}
        </div>
      ) : undefined
    }
  >
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        {drill.category && (
          <span className="rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-800">{drill.category}</span>
        )}
        {drill.durationMinutes ? (
          <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">{drill.durationMinutes} perc</span>
        ) : null}
        <span className="text-xs text-gray-400">
          {drill.createdByName} · {new Date(drill.updatedAt ?? drill.createdAt).toLocaleDateString('hu-HU')}
        </span>
      </div>

      {drill.diagram && <DrillThumbnail diagram={drill.diagram} title={drill.title} />}

      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Meta label="Létszám, szervezés" value={drill.players} />
        <Meta label="Eszközök" value={drill.equipment} />
        <Meta label="Ajánlott korosztály" value={drill.ageGroupHint} />
      </dl>

      {drill.description ? (
        <p className="whitespace-pre-line text-sm text-gray-700">{drill.description}</p>
      ) : (
        <p className="text-sm text-gray-400">Nincs leírás.</p>
      )}
    </div>
  </DrillSheet>
);

export default DrillDetailModal;
